/**
 * Upcoming tab — next 7 days, grouped by date.
 */

import { useMemo } from 'react';
import { Pill } from '@/components/primitives';
import { AppointmentCard } from '../components/AppointmentCard';
import { useUpcomingCalendar } from '../hooks/useUpcomingCalendar';
import type { Appointment } from '../types';

function dayKey(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

export function UpcomingTab(): JSX.Element {
  const { appointments, loading } = useUpcomingCalendar();

  const grouped = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const a of appointments) {
      const key = dayKey(a.startUtc);
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((x, y) => x.startUtc.localeCompare(y.startUtc));
    return Array.from(map.entries());
  }, [appointments]);

  return (
    <section className="appt-tab" aria-label="Upcoming appointments">
      <div className="appt-tab__bar">
        <div>
          <h2>Upcoming · next 7 days</h2>
        </div>
      </div>

      {loading && <div className="appt-tab__skeleton" role="status">Loading upcoming appointments…</div>}

      {!loading && grouped.length === 0 && (
        <div className="appt-empty">
          <Pill variant="muted">Nothing on the calendar in the next 7 days</Pill>
          <p>Calendar events you create or that customers self-book will appear here.</p>
        </div>
      )}

      {!loading && grouped.map(([day, items]) => (
        <div key={day} className="appt-day-group">
          <h3 className="appt-day-group__title">{day}</h3>
          <ul className="appt-card-list">
            {items.map((a) => (
              <li key={a.id}>
                <AppointmentCard appointment={a} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
